import * as Select from "@radix-ui/react-select";
import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePrefetchHandlers } from "~/components/build/Show/lib/usePrefetchHandlers";

import PipelineStateIcon from "~/components/shared/PipelineStateIcon/PipelineStateIcon";
import Icon from "~/components/shared/Icon";
import { BuildStates } from "~/constants/BuildStates";
import { useTrack } from "~/components/build/Show/lib/useTrack";
import { getBuildColor } from "~/components/build/Show/lib/getBuildColor";
import cable from "~/lib/cable";
import Indicator from "~/components/shared/Indicator";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import classNames from "classnames";
import {
  navigationQueryKey,
  navigationQueryOptions,
  NavigationData,
} from "~/components/build/Show/lib/queries/navigation";

type PipelineChannelMessage = {
  event: string;
  branch?: string;
  merge_queue_base_branch?: string;
};

interface Props {
  number: number;
  state: BuildStates;
  branchName: string;
  project: {
    id: string;
  };
  mergeQueueBaseBranch: string | null;
}

export default function BuildNumber({
  number,
  state,
  branchName,
  mergeQueueBaseBranch,
  project,
}: Props) {
  const { build } = useBuild();
  const queryClient = useQueryClient();

  const { data: navigationData } = useQuery(navigationQueryOptions(build));

  const { onMouseEnter, onMouseLeave } = usePrefetchHandlers({
    queries: [navigationQueryOptions(build)],
  });

  const { showIndicator, setShowIndicator } = useLatestBuildIndicator({
    projectId: project.id,
    branchName,
    mergeQueueBaseBranch,
    onBuildCreated: () => {
      queryClient.invalidateQueries({
        queryKey: navigationQueryKey(build.id),
        exact: true,
      });
    },
  });

  const handleOpen = useCallback(
    (open: boolean) => {
      if (open) {
        queryClient.invalidateQueries({
          queryKey: navigationQueryKey(build.id),
          exact: true,
        });
      } else {
        setShowIndicator(false);
      }
    },
    [queryClient, build.id, setShowIndicator],
  );

  return (
    <BuildNumberDropdown
      number={number}
      state={state}
      navigationData={navigationData ?? null}
      showIndicator={showIndicator}
      onOpenChange={handleOpen}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    />
  );
}

function useLatestBuildIndicator({
  projectId,
  branchName,
  mergeQueueBaseBranch,
  onBuildCreated,
}: {
  projectId: string;
  branchName: string;
  mergeQueueBaseBranch: string | null;
  onBuildCreated: () => void;
}) {
  const [showIndicator, setShowIndicator] = useState(false);

  useEffect(() => {
    const sub = cable.subscriptions.create(
      { channel: "Pipelines::PipelineChannel", uuid: projectId },
      {
        received({ event, branch, merge_queue_base_branch }: PipelineChannelMessage) {
          if (event === "build:created") {
            if (
              (branch && branch === branchName) ||
              (mergeQueueBaseBranch && merge_queue_base_branch === mergeQueueBaseBranch)
            ) {
              setShowIndicator(true);
              onBuildCreated();
            }
          }
        },
      },
    );

    return () => {
      sub.unsubscribe();
    };
  }, []);

  return { showIndicator, setShowIndicator };
}

function BuildNumberDropdown({
  number,
  state,
  navigationData,
  showIndicator,
  onOpenChange,
  onMouseEnter,
  onMouseLeave,
}: {
  number: number;
  state: BuildStates;
  navigationData: NavigationData | null;
  showIndicator: boolean;
  onOpenChange: (open: boolean) => void;
  onMouseEnter?: (event: React.MouseEvent) => void;
  onMouseLeave?: (event: React.MouseEvent) => void;
}) {
  const { build } = useBuild();

  const urlForBuild = useCallback(
    (buildNumber: number) => {
      return build.path.replace(/\/builds\/(\d+)/, `/builds/${buildNumber}`);
    },
    [build.path],
  );

  const track = useTrack();
  const navigate = useCallback(
    (url: string) => {
      const latest =
        navigationData &&
        navigationData.latestBranchBuild &&
        url.match(new RegExp(`/builds/${navigationData.latestBranchBuild.number}`, "i"))
          ? true
          : false;

      track("Open Different Build", {
        latest: latest,
      });

      window.location.href = url;
    },
    [navigationData],
  );

  return (
    <Select.Root
      value={`${number}`}
      defaultValue={`${number}`}
      onValueChange={navigate}
      onOpenChange={onOpenChange}
      disabled={!navigationData?.prevBranchBuild && !navigationData?.nextBranchBuild}
    >
      <Select.Trigger
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className="outline-hidden group relative flex items-center rounded-md transition-colors hover:underline disabled:hover:no-underline"
      >
        <Select.Value />
        <span className="flex w-5 items-center justify-center self-stretch transition-colors group-disabled:text-gray-500">
          <Icon icon="custom/outline/chevron-down" className="h-5 w-5" />
          {showIndicator && <Indicator className="absolute" />}
        </span>
      </Select.Trigger>

      <Select.Portal>
        <Select.Content className="z-50">
          <Select.Viewport className="rounded-sm bg-white shadow-md">
            {navigationData?.prevBranchBuild && (
              <Select.Item
                title="Go to previous build on this branch"
                value={urlForBuild(navigationData.prevBranchBuild.number)}
                className="hover:outline-hidden flex cursor-pointer items-center justify-between gap-2 px-2 py-1 hover:bg-purple-100 hover:text-purple-600 aria-[selected=true]:bg-purple-100 aria-[selected=true]:text-purple-600"
              >
                <span>Previous</span>
                <span className="flex items-center justify-between gap-1">
                  <Select.ItemText>#{navigationData.prevBranchBuild.number}</Select.ItemText>
                  <PipelineStateIcon
                    build={navigationData.prevBranchBuild}
                    style={{
                      color: getBuildColor(navigationData.prevBranchBuild).primaryColor,
                    }}
                  />
                </span>
              </Select.Item>
            )}

            <Select.Item
              value={`${number}`}
              className="hover:outline-hidden flex cursor-pointer items-center justify-between gap-2 px-2 py-1 hover:bg-purple-100 hover:text-purple-600 aria-[selected=true]:bg-purple-100 aria-[selected=true]:text-purple-600"
            >
              <span>Current</span>
              <span className="flex items-center gap-1">
                <Select.ItemText>#{number}</Select.ItemText>
                <PipelineStateIcon
                  build={{ state }}
                  style={{ color: getBuildColor({ state }).primaryColor }}
                />
              </span>
            </Select.Item>

            {navigationData?.nextBranchBuild &&
              navigationData.nextBranchBuild.number !==
                (navigationData.latestBranchBuild && navigationData.latestBranchBuild.number) && (
                <Select.Item
                  title="Go to next build on this branch"
                  value={urlForBuild(navigationData.nextBranchBuild.number)}
                  className="hover:outline-hidden flex cursor-pointer items-center justify-between gap-2 px-2 py-1 hover:bg-purple-100 hover:text-purple-600 aria-[selected=true]:bg-purple-100 aria-[selected=true]:text-purple-600"
                >
                  <span>Next</span>
                  <span className="flex items-center gap-1">
                    <Select.ItemText>#{navigationData.nextBranchBuild.number}</Select.ItemText>
                    <PipelineStateIcon
                      build={navigationData.nextBranchBuild}
                      style={{
                        color: getBuildColor(navigationData.nextBranchBuild).primaryColor,
                      }}
                    />
                  </span>
                </Select.Item>
              )}

            {navigationData?.latestBranchBuild &&
              navigationData.latestBranchBuild.number !== number && (
                <Select.Item
                  title="Go to latest build on this branch"
                  value={urlForBuild(navigationData.latestBranchBuild.number)}
                  className={classNames(
                    "hover:outline-hidden flex cursor-pointer items-center justify-between gap-2 px-2 py-1 hover:bg-purple-100 hover:text-purple-600 aria-[selected=true]:bg-purple-100 aria-[selected=true]:text-purple-600",
                    {
                      "font-semibold": showIndicator,
                    },
                  )}
                >
                  <span>Latest</span>

                  <span className="flex items-center gap-1">
                    <Select.ItemText>#{navigationData.latestBranchBuild.number}</Select.ItemText>

                    <PipelineStateIcon
                      build={navigationData.latestBranchBuild}
                      style={{
                        color: getBuildColor(navigationData.latestBranchBuild).primaryColor,
                      }}
                    />
                  </span>
                </Select.Item>
              )}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
